import { useState, useEffect, useMemo } from "react";
import ReactFlow, { Background, Controls } from "reactflow";
import "reactflow/dist/style.css";
import { Box, CircularProgress, Grid, TextField, MenuItem } from "@mui/material";
import MDBox from "components/MDBox";
import { useAuth } from "context/authContext";
import { MarkerType } from "reactflow";
import { Typography } from "@mui/material";
import { getActiveKpisByJobId, getKpiLinksWithNames, getMyScorecard } from "services/api";
import { useTranslation } from "react-i18next";
import KpiDialog from "components/ViewKpiDetailsDialog";
import { fieldStyles } from "services/utils";

import dagre from "dagre";
import { calculateKpiResult, CustomKpiNodeForCeo } from "services/utils";
``;
import MonthYearSelector from "components/DateSelector";
import { getKpisForStrategicRoadmap } from "services/api";

const buildGraphLayout = (links, kpiResults = [], onNodeClick, t) => {
  // Map KPI name ID to weighted result
  const finalResultMap = {};
  const resultColorMap = {};
  const trendMap = {};
  const kpiDataMap = {};
  const kpiResultMap = {};

  kpiResults.forEach((result) => {
    const { kpi, finalResult, resultColor } = result;
    finalResultMap[kpi.kpiNameId] = finalResult;
    resultColorMap[kpi.kpiNameId] = resultColor;
    trendMap[kpi.kpiNameId] = kpi.trend;
    kpiDataMap[kpi.kpiNameId] = kpi;
    kpiResultMap[kpi.kpiNameId] = result; // ✅ Full object needed for the dialog
  });

  const g = new dagre.graphlib.Graph(); //Initializes a Dagre graph object. used to define nodes and edges before running the layout.
  g.setGraph({ rankdir: "BT" });
  g.setDefaultEdgeLabel(() => ({}));

  const nodeWidth = 180;
  const nodeHeight = 60;
  const verticalSpacing = 200;

  Object.entries(kpiDataMap).forEach(([kpiNameId, kpi]) => {
    g.setNode(String(kpiNameId), {
      width: nodeWidth,
      height: nodeHeight,
      label: kpi.kpiName, // Optional: include label if needed
    });
  });

  // put links between nodes
  links?.forEach(([sourceId, , targetId]) => {
    g.setEdge(String(sourceId), String(targetId));
  });

  dagre.layout(g);

  //Define perspective categories and Y positions
  const perspectives = [
    "Finance Viability",
    "External StakeHolder",
    "Internal Process",
    "Internal StakeHolder",
  ];
  const perspectiveYPositions = perspectives.reduce((acc, name, index) => {
    acc[name] = index * verticalSpacing;
    return acc;
  }, {});

  // Count incoming links (arrows) to each KPI
  const incomingCountMap = {};

  links?.forEach(([, , targetId]) => {
    incomingCountMap[targetId] = (incomingCountMap[targetId] || 0) + 1;
  });

  const perspectiveNodeCounts = {};

  const rawNodes = Object.entries(kpiDataMap).map(([kpiNameId, kpi]) => {
    const perspectiveName = kpi.perspectiveName || "Uncategorized";
    const y = perspectiveYPositions[perspectiveName] || 0;
    const kpiResult = kpiResultMap[kpiNameId];
    if (!perspectiveNodeCounts[perspectiveName]) {
      perspectiveNodeCounts[perspectiveName] = 0;
    }

    const x = perspectiveNodeCounts[perspectiveName] * (nodeWidth + 60);
    perspectiveNodeCounts[perspectiveName]++;

    return {
      id: String(kpiNameId),
      type: "kpiNode",
      data: {
        label: kpi.kpiName,
        finalResult: finalResultMap[kpiNameId] ?? null,
        resultColor: resultColorMap[kpiNameId] ?? null,
        incomingCount: incomingCountMap[kpiNameId] || 0,
        trend: trendMap[kpiNameId] ?? null,
        // Include additional KPI data if needed
        weight: kpi.weight,
        target: kpi.target,
        achievement: kpi.achievement,
        perspectiveName: perspectiveName,
        kpiAssignmentId: kpi.kpiAssignmentId,
        onClick: () => {
          onNodeClick?.(kpiResult); // Only call if handler exists
        },
      },
      position: { x, y },
      sourcePosition: "top",
      targetPosition: "bottom",
    };
  });

  // Utility: generate HSL color from index
  const generateHSLColor = (index) => {
    const hue = (index * 137.508) % 360; // use golden angle for even distribution
    return `hsl(${hue}, 70%, 60%)`;
  };

  // Build source-to-color map
  const sourceColorMap = {};

  // Loop through all KPI relationships,generates the edges (connections) between KPI nodes
  const targetIncomingMap = {};

  const rawEdges = links.map(([sourceId, , targetId, , linkId], index) => {
    const handleIndex = targetIncomingMap[targetId] ?? 0; //gets how many incoming connections the target KPI node has.
    const handleId = `top-${handleIndex}`;
    targetIncomingMap[targetId] = handleIndex + 1;

    if (!sourceColorMap[sourceId]) {
      sourceColorMap[sourceId] = generateHSLColor(Object.keys(sourceColorMap).length);
    }

    return {
      id: `edge-${sourceId}-${targetId}-${handleIndex}`,
      source: String(sourceId),
      target: String(targetId),
      targetHandle: handleId,
      type: "straight",
      // type: 'step',
      data: {
        linkId,
      },
      style: {
        stroke: sourceColorMap[sourceId],
        strokeWidth: 3,
        strokeDasharray: "6 4",
        zIndex: 1200,
      },
      animated: true,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: sourceColorMap[sourceId],
        width: 10,
        height: 10,
      },
    };
  });

  // row header nodes

  const labelNodes = perspectives.map((perspective, index) => {
    const y = index * 200 + 20; // Adjust Y to better align with the center of your KPI nodes

    return {
      id: `label-${perspective}`,
      type: "input", // or a custom type if you want to render more complex JSX
      // data: { label: perspective },
      data: {
        // This matches your MenuItem logic exactly
        label: t(`user.assess.perspectives.${perspective}`),
      },
      position: { x: -330, y }, // Move it further left to provide visual space
      draggable: false,
      selectable: false,
      style: {
        width: 220, // Slightly wider to accommodate text and padding
        height: 60, // Taller for more presence
        background: "#ffffff", // Clean white background
        color: "#333", // Dark text
        border: "1px solid #A7DBD8", // Light teal border, consistent with KPI nodes
        borderLeft: "8px solid #009688", // Strong, prominent teal bar on the left
        borderRadius: "8px", // Match the rounded corners of your KPI nodes
        boxShadow: "2px 4px 8px rgba(0, 0, 0, 0.1)", // More pronounced shadow for elevation
        fontWeight: "600", // Bolder font
        fontSize: "15px", // Larger font size
        textAlign: "left", // Align text to the left after the teal bar
        paddingLeft: "15px", // Padding after the thick border
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start", // Align content to the start (left)
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        // Optional: Add a subtle text shadow for depth (be cautious with this)
        // textShadow: '0 1px 1px rgba(0,0,0,0.05)'
      },
    };
  });

  return {
    nodes: [...labelNodes, ...rawNodes],
    edges: rawEdges,
  };
};

function KpiLinks() {
  const [activeKpis, setActiveKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpiLinks, setKpiLinks] = useState([]); // kpi relations
  const { employee } = useAuth();
  const [kpisForCeo, setKpisForCeo] = useState([]); // kpi relations
  const { selectedMonth, setSelectedMonth, selectedYear, setSelectedYear, quarter, isQuarterly } =
    useAuth(); //selectedYear,setSelectedYear
  const { t, i18n } = useTranslation();
  const [selectedKpi, setSelectedKpi] = useState(null); // the selected KPI's details
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [kpiResultsForCeo, setKpiResultsForCeo] = useState([]);

  const handleKpiSave = (updatedKpi, setKpiResultsForCeo, setKpiDialogOpen) => {
    const finalResult = calculateKpiResult(
      updatedKpi.target,
      updatedKpi.achievement,
      updatedKpi.secondaryAchievement,
      updatedKpi.kpiFormula,
      updatedKpi.primaryPercentage
    );

    const weightedResult = calculateKpiResult(
      updatedKpi.target,
      updatedKpi.achievement,
      updatedKpi.secondaryAchievement,
      updatedKpi.kpiFormula,
      updatedKpi.primaryPercentage,
      updatedKpi.weight
    );

    const resultColor = getResultColor(finalResult, updatedKpi.achievement);

    setKpiResultsForCeo((prev) =>
      prev.map((result) =>
        result.kpi.kpiAssignmentId === updatedKpi.kpiAssignmentId
          ? {
              ...result,
              kpi: updatedKpi,
              finalResult: isFinite(finalResult) ? finalResult : 0,
              weightedResult: isFinite(weightedResult) ? weightedResult : 0,
              resultColor,
            }
          : result
      )
    );

    setKpiDialogOpen(false);
  };

  const getResultColor = (finalResult, ach) => {
    if (ach == null) return "grey";
    if (finalResult > 105) return "blue";
    if (finalResult >= 90 && finalResult <= 105) return "green";
    if (finalResult >= 80 && finalResult < 90) return "orange";
    if (finalResult >= 70 && finalResult < 80) return "yellow";
    return "red";
  };

  const handleNodeClick = (kpi) => {
    if (kpi?.kpi?.frequency && kpi?.kpi?.frequency !== "Monthly") return;
    setSelectedKpi(kpi); // your KPI state
    setKpiDialogOpen(true); // your dialog open state
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (employee?.role === 1) {
        setEmployeeId(employee.id);
        const kpiLinks = await getKpiLinksWithNames();
        if (kpiLinks != "error") setKpiLinks(kpiLinks); //kpiLinks has the source kpiNameId,source kpiName and target...
        const activeKpisData = await getActiveKpisByJobId(employee.jobId);
        if (activeKpisData != "error") setActiveKpis(activeKpisData);
        // active kpis has number of arrays, each has kpi assignemt id, kpi Name Id, kpi Name
        const myScorecarddata = await getKpisForStrategicRoadmap(
          employee.id,
          selectedMonth,
          selectedYear
        );
        // Returns kpi assignemt id, kpi Name Id, kpiWeight,kpiAssTarget,kpi Primary Per,Kpi Formula, kpiName, kpi Perspective, performance target,
        // performance achievement, per second achievement

        if (myScorecarddata !== "error") {
          const mapped = myScorecarddata.map((entry) => {
            const values = entry.split(",");
            return {
              kpiName: values[0],
              weight: parseFloat(values[1]),
              target: parseFloat(values[2]),
              achievement: values[3] != null && values[3] !== "null" ? parseFloat(values[3]) : null,
              perspectiveId: parseInt(values[4], 10),
              approvedTarget: isNaN(parseInt(values[5], 10)) ? null : parseInt(values[5], 10),
              approvedAchievement: isNaN(parseInt(values[6], 10)) ? null : parseInt(values[6], 10),
              kpiFormula: values[7],
              trend: parseFloat(values[8]) ? (parseFloat(values[8]) > 0 ? "up" : "down") : null,
              kpiAssignmentId: parseInt(values[9], 10),
              secondaryAchievement:
                values[10] != null && values[10] !== "null" ? parseFloat(values[10]) : null,
              performanceId: parseInt(values[11], 10) || null,
              selfAchievement: parseFloat(values[12]) || null,
              primaryPercentage: parseInt(values[13]) || null,
              kpiNameId: parseInt(values[14], 10),
              perspectiveName: values[15] !== "null" ? values[15] : null,
              frequency: values[17],
            };
          });

          setKpisForCeo(mapped);
        }
      }
    };

    fetchData().then((e) => {
      setLoading(false);
    });
  }, [employee, selectedMonth, selectedYear]);

  useEffect(() => {
    if (kpisForCeo) {
      const calculated = kpisForCeo
        .filter((kpi) => !kpi.perspectiveId || [3, 4, 5, 6].includes(kpi.perspectiveId))
        .map((kpi) => {
          let finalResult = calculateKpiResult(
            kpi.target,
            kpi.achievement,
            kpi.secondaryAchievement,
            kpi.kpiFormula,
            kpi.primaryPercentage
          );
          finalResult = isFinite(finalResult) ? finalResult : 0;

          let weightedResult = calculateKpiResult(
            kpi.target,
            kpi.achievement,
            kpi.secondaryAchievement,
            kpi.kpiFormula,
            kpi.primaryPercentage,
            kpi.weight
          );
          weightedResult = isFinite(weightedResult) ? weightedResult : 0;

          const resultColor = getResultColor(finalResult, kpi.achievement);

          return {
            kpi,
            finalResult,
            weightedResult,
            resultColor,
          };
        });

      setKpiResultsForCeo(calculated);
    }
  }, [kpisForCeo]);

  //Results are stored in a state variable in order to be updated later

  const nodeTypes = {
    kpiNode: CustomKpiNodeForCeo,
  };

  // avoid recomputation of the graph, re-run the buildGraphLayout function only if state changes

  const graphData = useMemo(() => {
    return buildGraphLayout(kpiLinks, kpiResultsForCeo, handleNodeClick, t);
  }, [activeKpis, kpiLinks, kpiResultsForCeo]);

  const nodes = graphData.nodes;
  const edges = graphData.edges;

  return (
    <MDBox p={3}>
      {loading ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress size={20} />
        </Box>
      ) : nodes.length === 0 ? (
        <Typography
          variant="h6"
          textAlign="center"
          sx={{ height: "500px", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          {t("user.kpiLinks.noData")} {/* Or any appropriate no data message */}
        </Typography>
      ) : (
        <MDBox height="500px" borderRadius="lg" sx={{ background: "#fff" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodeClick={handleNodeClick}
            fitView
          >
            <Background />
            <Controls />
          </ReactFlow>
        </MDBox>
      )}

      <KpiDialog
        open={kpiDialogOpen}
        onClose={() => setKpiDialogOpen(false)}
        kpi={selectedKpi}
        employeeId={employeeId}
        onSave={(updatedKpi) => handleKpiSave(updatedKpi, setKpiResultsForCeo, setKpiDialogOpen)}
      />
    </MDBox>
  );
}

export default KpiLinks;
